import type { CampoPlantilla } from "./tipos"

export type ValoresCampos = Record<string, string | boolean>

/**
 * Lo que una plantilla pide al generarse, además de la ficha: fechas, horas, motivo,
 * el equipo que se entrega…
 *
 * ⚠ **Sólo pinta los campos; no valida.** Quien valida es el servidor, contra la
 * definición de la plantilla —el mismo que compone el texto—: con dos validaciones, la
 * primera vez que discreparan nadie sabría cuál manda. Aquí sólo se marca lo obligatorio.
 */
export function FormularioCampos({
  campos,
  valores,
  onCambiar,
  prefijo = "campo",
}: {
  campos: CampoPlantilla[]
  valores: ValoresCampos
  onCambiar: (valores: ValoresCampos) => void
  prefijo?: string
}) {
  if (campos.length === 0) {
    return (
      <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
        Este documento no pide nada más: se llena entero con la ficha.
      </p>
    )
  }
  const poner = (clave: string, valor: string | boolean) =>
    onCambiar({ ...valores, [clave]: valor })

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {campos.map((c) => {
        const id = `${prefijo}-${c.clave}`
        const valor = valores[c.clave]
        const ancho = c.tipo === "parrafo" ? "sm:col-span-2" : ""
        const rotulo = (
          <label htmlFor={id} className="mb-1 block text-xs font-medium uppercase">
            {c.etiqueta}
            {c.obligatorio && (
              <span style={{ color: "var(--destructive)" }} aria-hidden="true">
                {" "}
                *
              </span>
            )}
          </label>
        )
        if (c.tipo === "si_no") {
          return (
            <label key={c.clave} className={`flex items-center gap-2 text-sm ${ancho}`}>
              <input
                id={id}
                type="checkbox"
                checked={valor === true}
                onChange={(e) => poner(c.clave, e.target.checked)}
              />
              {c.etiqueta}
            </label>
          )
        }
        return (
          <div key={c.clave} className={ancho}>
            {rotulo}
            {c.tipo === "parrafo" ? (
              <textarea
                id={id}
                rows={3}
                className="campo"
                required={c.obligatorio}
                value={String(valor ?? "")}
                onChange={(e) => poner(c.clave, e.target.value)}
              />
            ) : c.tipo === "lista" ? (
              <select
                id={id}
                className="campo"
                required={c.obligatorio}
                value={String(valor ?? "")}
                onChange={(e) => poner(c.clave, e.target.value)}
              >
                <option value="">Elegir…</option>
                {(c.opciones ?? []).map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={id}
                className={
                  c.tipo === "numero" || c.tipo === "monto" ? "campo tabular-nums" : "campo"
                }
                required={c.obligatorio}
                type={c.tipo === "fecha" ? "date" : c.tipo === "hora" ? "time" : "text"}
                inputMode={c.tipo === "numero" || c.tipo === "monto" ? "decimal" : undefined}
                value={String(valor ?? "")}
                onChange={(e) => poner(c.clave, e.target.value)}
              />
            )}
            {c.ayuda && (
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                {c.ayuda}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}
